import EventEmitter from 'events/events.js';
import assert from 'minimalistic-assert';
import proxyFreeze from '@exodus/proxy-freeze';
import { LifecycleHook as Hook } from '../constants/index.js';
// Because we are forced to restart wallet after deleting/importing, we need to store certain flags to persist state between executions
// Triggers deletetion logic when wallet starts.
// Set as true on delete method is called, and set to false when wallet is restarted
const DELETE_FLAG = 'deleteFlag';
// Triggers import logic when wallet starts. Used when overwriting existing wallet (not importing one from scratch)
// Set as true on import method is called, and set to false when wallet is restarted
const IMPORT_FLAG = 'importFlag';
// Triggers restore logic. We need to do it after any import, no matter if there was a previous wallet or not
// Set as true on import method is called, and set to false after restore is completed
const RESTORE_FLAG = 'restoreFlag';
const HOOKS = new Set(Object.values(Hook));
const MODULE_ID = 'application';
const passphraseCachePlaceholder = {
    changeTtl: () => Promise.resolve(),
    clear: () => Promise.resolve(),
    scheduleClear: () => Promise.resolve(),
    get: () => Promise.resolve(''),
    set: () => Promise.resolve(),
};
export class Application extends EventEmitter {
    #hooks = {};
    #hookNeedsSorting = {};
    #logger;
    #eventLog;
    #wallet;
    #lockedAtom;
    #backedUpAtom;
    #passphraseCache;
    #applicationStarted;
    #resolveStart = () => Promise.resolve();
    #flagsStorage;
    #storage;
    #errorTracking;
    constructor({ wallet, unsafeStorage, lockedAtom, backedUpAtom, passphraseCache = passphraseCachePlaceholder, logger, eventLog, errorTracking, }) {
        super();
        this.#logger = logger;
        this.#eventLog = eventLog;
        this.#wallet = wallet;
        this.#lockedAtom = lockedAtom;
        this.#backedUpAtom = backedUpAtom;
        this.#passphraseCache = passphraseCache;
        this.#flagsStorage = unsafeStorage.namespace('flags');
        this.#storage = unsafeStorage.namespace('application');
        this.#errorTracking = errorTracking;
        this.#applicationStarted = new Promise((resolve) => (this.#resolveStart = resolve));
        super.setMaxListeners(Number.POSITIVE_INFINITY);
    }
    emit = (name, ...args) => {
        const isFreezable = (val) => val && typeof val === 'object';
        return super.emit(name, ...args.map((arg) => (isFreezable(arg) ? proxyFreeze(arg) : arg)));
    };
    start = async ({ restoring, importing } = {}) => {
        const [deleteFlag, importFlag] = await this.#flagsStorage.batchGet([DELETE_FLAG, IMPORT_FLAG]);
        if (restoring) {
            await this.#flagsStorage.set(RESTORE_FLAG, true);
        }
        const isDeleting = !!deleteFlag;
        const isImporting = importing || !!importFlag;
        if (isDeleting)
            await this.#wallet.clear();
        if (isDeleting || isImporting) {
            await this.#flagsStorage.batchDelete([DELETE_FLAG, IMPORT_FLAG]);
            await this.#passphraseCache.clear();
        }
        const walletExists = await this.#wallet.exists();
        if (isImporting || !walletExists) {
            await this.#eventLog.record({
                event: 'app_state_clear_requested',
                applicationWillFireClearHookOpts: { isImporting, isDeleting, walletExists },
            });
            await this.fire(Hook.Clear, null, { concurrent: true });
        }
        if (isImporting) {
            const params = await this.#storage.get('importParams');
            await this.fire(Hook.Import, params);
            await this.#storage.delete('importParams');
        }
        const [hasPassphraseSet, isLocked, isBackedUp, isRestoring] = await Promise.all([
            this.#wallet.hasPassphraseSet(),
            this.#wallet.isLocked(),
            this.isBackedUp(),
            this.isRestoring(),
        ]);
        await this.fire(Hook.Start, {
            walletExists,
            hasPassphraseSet,
            isLocked,
            isBackedUp,
            isRestoring,
        });
        await this.#autoUnlock();
        const locked = await this.#wallet.isLocked();
        this.#logger.log('wallet is locked', locked);
        this.#resolveStart();
    };
    load = async () => {
        await this.#applicationStarted;
        const [walletExists, hasPassphraseSet, isLocked, isBackedUp, isRestoring] = await Promise.all([
            this.#wallet.exists(),
            this.#wallet.hasPassphraseSet(),
            this.#wallet.isLocked(),
            this.isBackedUp(),
            this.isRestoring(),
        ]);
        await this.fire(Hook.Load, {
            walletExists,
            hasPassphraseSet,
            isLocked,
            isBackedUp,
            isRestoring,
        });
    };
    unload = async () => {
        await this.#applicationStarted;
        await this.#passphraseCache.scheduleClear();
        await this.fire(Hook.Unload);
    };
    hook = (hookName, listener) => {
        assert(HOOKS.has(hookName), `no such hook: ${hookName}`);
        if (!this.#hooks[hookName]) {
            this.#hooks[hookName] = [];
        }
        this.#hooks[hookName].push(listener);
        this.#hookNeedsSorting[hookName] = true;
    };
    #executeHooks = async (hooksFns, concurrent = false) => {
        if (concurrent) {
            return Promise.allSettled(hooksFns.map((fn) => fn()));
        }
        for (const hook of hooksFns) {
            await hook();
        }
    };
    fire = async (hookName, params, { concurrent } = {}) => {
        assert(HOOKS.has(hookName), `no such hook: ${hookName}`);
        this.#logger.debug(`firing hooks ${concurrent ? 'concurrently' : 'sequentially'}`, hookName);
        const hooks = this.#hooks[hookName] || [];
        if (this.#hookNeedsSorting[hookName]) {
            hooks.sort((a, b) => (b.priority || 0) - (a.priority || 0));
            this.#hookNeedsSorting[hookName] = false;
        }
        const hookFns = hooks.map((hook) => async () => {
            try {
                await hook(params);
            }
            catch (err) {
                this.#logger.error(`application lifecycle hook failed: ${hookName}`, hook.name, params, err);
                throw err;
            }
        });
        await this.#executeHooks(hookFns, concurrent);
        this.emit(hookName, params);
    };
    create = async (opts) => {
        this.#logger.log('creating wallet');
        await this.#applicationStarted;
        const createResult = await this.#wallet.create(opts);
        const isLocked = await this.#wallet.isLocked();
        await this.fire(Hook.Create, {
            hasPassphraseSet: !!opts?.passphrase,
            isBackedUp: false,
            isLocked,
            isRestoring: false,
            walletExists: true,
            seedId: createResult.seedId, // will be deprecated
            createResult,
        });
    };
    import = async (opts) => {
        this.#logger.log('importing wallet');
        await this.#eventLog.record({
            event: 'restore_wallet',
            applicationImportOpts: {
                forceRestart: opts.forceRestart,
                forgotPassphrase: opts.forgotPassphrase,
                backupType: opts.backupType,
            },
        });
        await this.#flagsStorage.set(RESTORE_FLAG, true);
        await this.#applicationStarted;
        const walletExists = await this.#wallet.exists();
        const { forceRestart, compatibilityMode, backupType, forgotPassphrase, ...wallet } = opts;
        if (backupType) {
            await this.#storage.set('backupType', backupType);
        }
        await this.fire(Hook.PreImport, { backupType, walletExists, forgotPassphrase });
        const importResult = await this.#wallet.import(wallet);
        const importParams = {
            compatibilityMode,
            backupType,
            importResult,
            seedId: importResult.seedId, // will be deprecated
        };
        if (forceRestart || walletExists) {
            await this.#flagsStorage.set(IMPORT_FLAG, true);
            await this.#storage.set('importParams', importParams);
            await this.fire(Hook.Restart, { reason: 'import', backupType, forgotPassphrase });
        }
        else {
            await this.fire(Hook.Import, importParams);
            this.#logger.log('wallet imported');
        }
    };
    restart = async () => {
        await this.fire(Hook.Restart, { reason: 'restart-method' });
    };
    getMnemonic = async (opts) => this.#wallet.getMnemonic(opts);
    setBackedUp = async () => {
        await this.#backedUpAtom.set(true);
    };
    lock = async () => {
        this.#logger.log('locking');
        await this.#applicationStarted;
        await this.#wallet.lock();
        await this.#lockedAtom.set(true);
        await this.#passphraseCache.clear();
        await this.fire(Hook.Lock);
        this.#logger.log('locked');
    };
    #restoreIfNeeded = async () => {
        try {
            const isRestoring = await this.isRestoring();
            if (isRestoring) {
                const backupType = await this.#storage.get('backupType');
                await this.fire(Hook.Restore, { backupType });
                await this.#flagsStorage.delete(RESTORE_FLAG);
                await this.fire(Hook.RestoreCompleted, { backupType });
                await this.#storage.delete('backupType');
            }
            this.fire(Hook.AssetsSynced).catch((e) => {
                this.#logger.warn('failed to execute onAssetsSynced hooks', e);
            });
        }
        catch (e) {
            this.#logger.error(e);
            this.#errorTracking
                .track({ error: e, namespace: 'application', context: 'restoreIfNeeded' })
                .catch((e) => this.#logger.error(e));
        }
    };
    #autoUnlock = async () => {
        const walletLocked = await this.#wallet.isLocked();
        const passphrase = await this.#passphraseCache.get();
        if (!walletLocked || !passphrase)
            return;
        try {
            this.#logger.log('unlocking with cache');
            await this.#wallet.unlock({ passphrase });
            await this.#lockedAtom.set(false);
            await this.fire(Hook.Migrate);
            await this.fire(Hook.Unlock);
            void this.#restoreIfNeeded();
            this.#logger.log('unlocked with cache');
        }
        catch (err) {
            this.#logger.error('failed to unlock, outdated cached passphrase?', err);
        }
    };
    unlock = async (opts) => {
        this.#logger.log('unlocking');
        await this.#applicationStarted;
        await this.#wallet.unlock(opts);
        await this.#lockedAtom.set(false);
        await this.fire(Hook.Migrate);
        await this.fire(Hook.Unlock);
        void this.#restoreIfNeeded();
        if (opts?.passphrase) {
            void this.#passphraseCache.set(opts.passphrase);
        }
        this.#logger.log('unlocked');
    };
    changePassphrase = async (opts) => {
        this.#logger.log('changing passphrase');
        const { currentPassphrase, newPassphrase } = opts;
        await this.#applicationStarted;
        await this.#wallet.changePassphrase({ currentPassphrase, newPassphrase });
        await this.#passphraseCache.set(newPassphrase);
        await this.fire(Hook.ChangePassphrase);
        this.#logger.log('passphrase changed');
    };
    delete = async (opts = {}) => {
        const { forgotPassphrase, restartOptions } = opts;
        await this.#flagsStorage.set(DELETE_FLAG, true);
        await this.#flagsStorage.delete(RESTORE_FLAG);
        await this.fire(Hook.Restart, { ...restartOptions, reason: 'delete', forgotPassphrase });
    };
    stop = async () => {
        await this.fire(Hook.Stop);
    };
    changeLockTimer = async ({ ttl }) => {
        return this.#passphraseCache.changeTtl(ttl);
    };
    restartAutoLockTimer = async () => {
        await this.#passphraseCache.scheduleClear();
    };
    isRestoring = async () => {
        return Boolean(await this.#flagsStorage.get(RESTORE_FLAG));
    };
    // TODO: stop emitting this on hooks?
    isBackedUp = async () => {
        return this.#backedUpAtom.get();
    };
}
const createApplicationModule = (args) => new Application({ ...args });
const applicationModuleDefinition = {
    id: MODULE_ID,
    type: 'module',
    factory: createApplicationModule,
    dependencies: [
        'wallet',
        'unsafeStorage',
        'lockedAtom',
        'backedUpAtom',
        'passphraseCache?',
        'logger',
        'eventLog',
        'errorTracking',
    ],
    public: true,
};
export default applicationModuleDefinition;
